import React from 'react';
import styled from 'styled-components';
import {useParams} from 'react-router';
import {Quill, QuillWrapper, StatusEclipse} from 'components/Ui';
import {
  Button,
  DatePicker,
  Select,
  TimePicker,
  Upload,
  Tooltip,
} from 'components/antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import moment from 'moment';
import telCategoryColor from 'helpers/telCategoryColor';
import {Paperclip, Cross} from 'components/Ui/Icon';
import {
  useCreateTelCommentActivityMutation,
  useProjectQuery,
  usePreleadProjectQuery,
} from 'api';
import TimelineItem from 'components/TimelineItem';
import usePreleadProject from 'hooks/usePreleadProject';
import ReceptionNgModal from './ReceptionNgModal';
import ContactNgModal from './ContactNgModal';
import CallNgModal from './CallNgModal';
import {Refresh} from 'components/Ui/Icon';

export default () => {
  const {projectId, preleadProjectId} = useParams<{
    projectId: string;
    preleadProjectId: string;
  }>();
  const {timeLineTelActivities} = usePreleadProject(preleadProjectId);
  const {data: {project: {telStatuses = []} = {}} = {}} = useProjectQuery({
    variables: {uuid: projectId},
    skip: !projectId,
  });
  const [comment, setComment] = React.useState('');
  const [bodyPlainText, setBodyPlainText] = React.useState(null);
  const [date, setDate] = React.useState(moment().format('YYYY/MM/DD'));
  const [time, setTime] = React.useState(moment().format('HH:mm'));
  const [status, setStatus] = React.useState(null);
  const [ngCallModalVisible, setCallNgModalVisible] = React.useState(false);
  const [callNgReason, setCallNgReason] = React.useState(null);
  const [ngReceptionModalVisible, setReceptionNgModalVisible] =
    React.useState(false);
  const [receptionNgReason, setReceptionNgReason] = React.useState(null);
  const [ngContactModalVisible, setContactNgModalVisible] =
    React.useState(false);
  const [contactNgReason, setContactNgReason] = React.useState(null);
  const [files, setFiles] = React.useState([]);

  React.useEffect(() => {
    if (!status) return;

    const telStatus = telStatuses.find((telStatus) => telStatus.id === status);
    if (!telStatus) return;

    if (telStatus?.category === 'call_ng') {
      setCallNgModalVisible(true);
    } else if (telStatus?.category === 'reception_ng') {
      setReceptionNgModalVisible(true);
    } else if (telStatus?.category === 'contact_ng') {
      setContactNgModalVisible(true);
    }

    setCallNgReason(null);
    setReceptionNgReason(null);
    setContactNgReason(null);
  }, [status]);

  const [createTelCommentActivity, {loading}] =
    useCreateTelCommentActivityMutation({
      onCompleted: () => {
        setComment('');
        setBodyPlainText(null);
        setDate(moment().format('YYYY/MM/DD'));
        setTime(moment().format('HH:mm'));
        setStatus(null);
        setFiles([]);
      },
    });
  const addTelActivity = () => {
    const recordedAt = moment(`${date} ${time}`, 'YYYY/MM/DD HH:mm');

    createTelCommentActivity({
      variables: {
        uuid: preleadProjectId,
        files,
        telCommentInput: {
          body: comment === `<p><br></p>` ? '' : comment,
          bodyPlainText: bodyPlainText,
          recordedAt,
        },
        telStatusChangeInput: {
          clientTelStatusId: status,
          projectCallNgReasonId: callNgReason?.id,
          projectReceptionNgReasonId: receptionNgReason?.id,
          projectContactNgReasonId: contactNgReason?.id,
        },
      },
    });
  };

  const {
    data: {preleadProject: {prelead: {telActivities = []} = {}} = {}} = {},
    refetch,
    loading: loadingPreleadProject,
  } = usePreleadProjectQuery({
    variables: {uuid: preleadProjectId},
    skip: !preleadProjectId,
    notifyOnNetworkStatusChange: true,
  });

  if (!preleadProjectId) return null;

  return (
    <Container>
      <Actions>
        <Button
          onClick={() => refetch()}
          icon={<Refresh spin={loadingPreleadProject} />}
          disabled={loadingPreleadProject}
        />
      </Actions>
      <QuillWrapper>
        <Quill
          placeholder="XX様とお話。〇〇様へ繋いでいただき、折返しご連絡を依頼。メールもご確認いただけるようにお願いしました。"
          theme="snow"
          value={comment}
          onChange={(content, delta, source, editor) => {
            setComment(content);
            setBodyPlainText(editor.getText());
          }}
          modules={{
            toolbar: [
              [{header: [1, 2, 3, false]}],
              ['bold', 'italic', 'underline'],
              ['code-block'],
              ['link'],
            ],
          }}
          formats={[
            'header',
            'list',
            'bold',
            'italic',
            'underline',
            'strike',
            'color',
            'background',
            'code-block',
            'link',
          ]}
          bounds={'#quill-container'}
        />
        <div id="quill-container" />
        {files.map((file) => (
          <File key={file.uid}>
            <FileName>{file.name}</FileName>
            <div onClick={() => setFiles(files.filter((f) => f !== file))}>
              <Cross />
            </div>
          </File>
        ))}
        <FormActions>
          <StatusSelect
            value={status}
            onSelect={(value: any) => setStatus(value)}
            dropdownMatchSelectWidth={false}
            dropdownClassName="tel-status-dropdown"
            optionLabelProp="label">
            {telStatuses?.map((telStatus) => (
              <StatusSelect.Option
                value={telStatus.id}
                key={telStatus.id}
                label={
                  <React.Fragment>
                    <StatusEclipse
                      color={telCategoryColor(telStatus.category)}
                    />
                    &nbsp; {telStatus.name}
                    {callNgReason && `  |  ${callNgReason.reason}`}
                    {receptionNgReason && `  |  ${receptionNgReason.reason}`}
                    {contactNgReason && `  |  ${contactNgReason.reason}`}
                  </React.Fragment>
                }>
                <StatusEclipse color={telCategoryColor(telStatus.category)} />
                &nbsp; {telStatus.name}
              </StatusSelect.Option>
            ))}
          </StatusSelect>
          <DatePicker
            allowClear={false}
            defaultValue={moment()}
            format="YYYY/MM/DD"
            value={moment(date, 'YYYY/MM/DD')}
            locale={locale}
            style={{width: '110px'}}
            onChange={(_, dateText) => setDate(dateText)}
          />
          <TimePicker
            allowClear={false}
            defaultValue={moment()}
            value={moment(time, 'HH:mm')}
            format="HH:mm"
            minuteStep={15}
            locale={locale}
            style={{width: '75px'}}
            onChange={(_, timeText) => setTime(timeText)}
          />
          <Upload
            name={'files'}
            customRequest={() => false}
            showUploadList={false}
            onChange={(info) => {
              const addedFile = info.file.originFileObj;
              const upload_size_limit = 25000000; // 25MB

              let fileSize = addedFile.size;
              files.forEach((file) => (fileSize += file.size));
              if (fileSize > upload_size_limit) {
                alert(
                  'ファイルの容量が25MBを超えています。\n合計25MBまでの添付が可能です。',
                );
                return null;
              }

              setFiles([...files, addedFile]);
            }}>
            <Tooltip
              title="ファイルを添付(25MBまで)"
              color={'var(--text-color-0)'}
              placement="top">
              <Button icon={<Paperclip />} style={{marginRight: 10}} />
            </Tooltip>
          </Upload>
          <Button
            disabled={
              (!comment.replace(/<\/?[^>]+(>|$)/g, '') &&
                !status &&
                files.length === 0) ||
              loading
            }
            onClick={addTelActivity}>
            登録
          </Button>
        </FormActions>
      </QuillWrapper>
      {timeLineTelActivities(telActivities).map((activity) => (
        <TimelineItem key={activity.uuid} activity={activity} />
      ))}

      {ngCallModalVisible && (
        <CallNgModal
          isModalVisible={ngCallModalVisible}
          setModalVisible={setCallNgModalVisible}
          ngReason={callNgReason}
          setNgReason={setCallNgReason}
        />
      )}

      {ngReceptionModalVisible && (
        <ReceptionNgModal
          isModalVisible={ngReceptionModalVisible}
          setModalVisible={setReceptionNgModalVisible}
          ngReason={receptionNgReason}
          setNgReason={setReceptionNgReason}
        />
      )}

      {ngContactModalVisible && (
        <ContactNgModal
          isModalVisible={ngContactModalVisible}
          setModalVisible={setContactNgModalVisible}
          ngReason={contactNgReason}
          setNgReason={setContactNgReason}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  .ant-btn > svg {
    margin-top: 4px;
  }
`;

const FormActions = styled.div`
  margin-top: 10px;
`;

const StatusSelect = styled(Select)`
  flex: 1;

  .ant-select-selector {
    max-width: 232px;
  }

  .ant-select-arrow {
    svg {
      margin-left: 5px;
      width: 10px;
      height: 10px;

      path {
        fill: var(--text-color-1);
      }
    }
  }
`;

const File = styled.div`
  height: 30px;
  background: #f3f5f7;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px 10px;

  svg {
    cursor: pointer;
    margin-right: 10px;
    width: 7px;
    height: 7px;

    path {
      stroke: var(--text-color-2);
      stroke-width: 1.5px;
    }
  }
`;

const FileName = styled.div`
  width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 15px;
  font-weight: bold;
  font-size: 12px;
  color: var(--primary);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;
